// Suivre un groupe ou une équipe (D-110) : ce que l'app demande à la base, et le petit compte d'un abonné
// depuis la page publique (fonction serveur follow-signup, puis le code de connexion habituel).
import { supabase, supabaseAnonKey, supabaseUrl } from './supabase';
import type { FollowState } from '../domain/follow';

const db = () => {
  if (!supabase) throw new Error('Base non configurée');
  return supabase;
};
async function must<T>(q: PromiseLike<{ data: T; error: { message: string; code?: string } | null }>): Promise<T> {
  const { data, error } = await q;
  if (error) throw Object.assign(new Error(error.message), { code: error.code });
  return data;
}

/** Mes suivis dans un club (par son identifiant, ou l'adresse de sa page publique) ; null si je n'y ai pas accès. */
export const followState = async (by: { club?: string; slug?: string }) =>
  (await must(db().rpc('follow_state', { p_club: by.club ?? null, p_slug: by.slug ?? null }))) as FollowState | null;

export interface FollowChoice { squad: string | null; result: boolean; changes: boolean; eve: boolean }
/** Suivre (ou régler) un groupe ; tout décoché : ne plus le suivre. */
export const followSet = async (teamId: string, c: FollowChoice) =>
  (await must(db().rpc('follow_set', { p_team: teamId, p_squad: c.squad, p_result: c.result, p_changes: c.changes, p_eve: c.eve }))) as FollowState;
/** « Ne plus suivre » ; un suivi d'office est coupé. */
export const followUnset = async (teamId: string) => (await must(db().rpc('follow_unset', { p_team: teamId }))) as FollowState;
/** L'e-mail de la journée (sans notifications). */
export const followEmailSet = (clubId: string, on: boolean) => must(db().rpc('follow_email_set', { p_club: clubId, p_on: on }));
/** Les matchs que je suis entre deux dates (★). */
export async function followEventIds(clubId: string, from: string, to: string): Promise<Set<string>> {
  const ids = (await must(db().rpc('follow_event_ids', { p_club: clubId, p_from: from, p_to: to }))) as unknown;
  return new Set(((ids ?? []) as (string | { follow_event_ids: string })[]).map(x => (typeof x === 'string' ? x : x.follow_event_ids)));
}

export interface FollowerClub { id: string; name: string; short_name: string | null; logo_url: string | null; primary_color: string | null; secondary_color: string | null; public_slug: string | null; timezone: string }
/** Les clubs dont je suis abonné sans en être membre. */
export const followerClubs = async () => ((await must(db().rpc('follower_clubs'))) ?? []) as FollowerClub[];

export interface FollowerMatch {
  event_id: string; starts_at: string; team_id: string; group_name: string; team_name: string; gender: string | null; opponent: string;
  is_home: boolean | null; location: string | null; competition: string; score_us: number | null; score_them: number | null;
  forfeit: 'us' | 'them' | null; opponent_org: string | null; followed: boolean;
}
/** Les matchs du club pour un abonné : ni noms, ni mots ; un score seulement confirmé. */
export const followerBoard = async (clubId: string, from: string, to: string) =>
  ((await must(db().rpc('follower_match_board', { p_club: clubId, p_from: from, p_to: to }))) ?? []) as FollowerMatch[];

/** Un abonné demande à devenir supporter du club (le bureau l'accepte, D-064). false : déjà demandé. */
export const followerAskSupporter = async (clubId: string, name: string | null) =>
  (await must(db().rpc('follower_ask_supporter', { p_club: clubId, p_name: name }))) as boolean;

/** Le lien d'arrêt des e-mails (sans connexion) : le club, et si l'e-mail est encore coché ; null si le lien n'est plus bon. */
export const followMailInfo = async (token: string) =>
  (await must(db().rpc('follow_mail_info', { p_token: token }))) as { club: string; on: boolean; short_name: string | null; color: string | null; logo: string | null } | null;
/** Le lien d'arrêt des e-mails (sans connexion) : le nom du club, ou null si le lien n'est plus bon. */
export const followMailStop = async (token: string) => (await must(db().rpc('follow_mail_stop', { p_token: token }))) as { club: string } | null;

/** Le petit compte d'un abonné (page publique) : le compte de cette adresse est prêt, l'app envoie ensuite le code. */
export async function followSignup(slug: string, email: string, website = ''): Promise<'ready' | 'too_many' | 'invalid' | 'error'> {
  let r: Response;
  try {
    r = await fetch(`${supabaseUrl}/functions/v1/follow-signup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', apikey: supabaseAnonKey!, Authorization: `Bearer ${supabaseAnonKey}` },
      body: JSON.stringify({ slug, email, website }),
    });
  } catch { return 'error'; }
  if (r.status === 429) return 'too_many';
  if (r.status === 400) return 'invalid';
  return r.ok ? 'ready' : 'error';
}
